"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";

type StartHereFocus = {
  id: string | null;
  title: string;
  why_now: string;
  confidence: "high" | "medium";
  effort_minutes?: number | null;
  goal?: {
    id: string;
    title: string;
  } | null;
  source_label: string;
  timing_label?: string | null;
  // Industrial-standard active-block flow: when the focus came from
  // a scheduled block whose window includes "now", we want the timer
  // to auto-start with the remaining minutes and auto-complete at
  // the block's end_time without forcing the user to tap Begin then
  // Done. These two fields are set ONLY for that case.
  auto_start_minutes?: number | null;
  auto_complete_at_end?: boolean;
};

interface Props {
  focus: StartHereFocus | null;
  onComplete?: (id: string) => Promise<void> | void;
  onSkip?: (id: string) => Promise<void> | void;
  // Number of tasks the user has finished today, used in the celebration
  // overlay's "Day · N done" eyebrow. Optional — celebration still fires
  // without it; the eyebrow just hides.
  completedToday?: number;
}

type TimerState = "idle" | "running" | "done" | "celebrating";

// ~1.4s celebration window before the parent removes this focus and the
// next one slides in. Lifted as a constant so the holdRef cleanup and
// the visual timeline stay in sync.
const CELEBRATION_HOLD_MS = 1400;

function formatTimeRange(startMinutes: number, durationMin: number): string {
  // Cosmetic helper for the "FOR YOUR X GOAL · 8:15 — 8:45" footer row.
  // startMinutes is "minutes since midnight" so we can pure-format without
  // pulling in a date library.
  const fmt = (m: number) => {
    const h24 = Math.floor(m / 60) % 24;
    const mm = m % 60;
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${h12}:${String(mm).padStart(2, "0")}`;
  };
  return `${fmt(startMinutes)} \u2014 ${fmt(startMinutes + durationMin)}`;
}

function cleanWhyNow(text: string): string {
  // Remove URLs
  let cleaned = text.replace(/https?:\/\/\S+/g, "").trim();
  // Remove internal route/system text
  cleaned = cleaned.replace(/Suggested route:.*$/i, "").trim();
  // Remove "Why now:" prefix if it got mixed in
  cleaned = cleaned.replace(/^Why now:\s*/i, "").trim();
  // If nothing meaningful left, return a generic message
  if (!cleaned || cleaned.length < 5) return "This is the next thing waiting for your attention.";
  // Capitalize first letter
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function RestartBlock({ focus, onComplete, onSkip, completedToday }: Props) {
  const [completing, setCompleting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [timerState, setTimerState] = useState<TimerState>("idle");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const totalSeconds = useRef(0);
  // Tasks may not carry an estimate. We don't fabricate one — the
  // button label only shows a duration when the task itself has a
  // real `effort_minutes` value or the user has explicitly picked
  // one from the duration preset.
  const taskMinutes = focus?.effort_minutes ?? null;
  const [chosenMinutes, setChosenMinutes] = useState<number | null>(null);
  const [showDurationPicker, setShowDurationPicker] = useState(false);
  const effectiveMinutes = taskMinutes ?? chosenMinutes;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const celebrationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cancel any in-flight celebration if this card unmounts (e.g. parent
  // swaps the focus) so we never call onComplete on a dead row.
  useEffect(() => {
    return () => {
      if (celebrationTimeoutRef.current) {
        clearTimeout(celebrationTimeoutRef.current);
      }
    };
  }, []);

  // Reset all state when focus changes
  useEffect(() => {
    setCompleting(false);
    setTimerState("idle");
    setSecondsLeft(0);
    setChosenMinutes(null);
    setShowDurationPicker(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [focus?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const startTimer = useCallback((minutes: number) => {
    if (!minutes || minutes <= 0) return;
    const total = minutes * 60;
    totalSeconds.current = total;
    setSecondsLeft(total);
    setTimerState("running");

    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;
          setTimerState("done");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Auto-start with separate "remaining now" and "full block" seconds.
  // The progress bar reads `1 - secondsLeft / totalSeconds.current`,
  // so when a block is half-elapsed already we want
  // totalSeconds = full block AND secondsLeft = remaining. Without
  // this split the bar treats "remaining" as the whole pie and
  // shows ~0% even when half the block is gone.
  const startTimerWithRemaining = useCallback(
    (remainingSeconds: number, fullBlockSeconds: number) => {
      if (remainingSeconds <= 0 || fullBlockSeconds <= 0) return;
      totalSeconds.current = fullBlockSeconds;
      setSecondsLeft(Math.min(remainingSeconds, fullBlockSeconds));
      setTimerState("running");
      intervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
            setTimerState("done");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [],
  );

  // Active-block auto-start: when the focus carries
  // ``auto_start_minutes`` (we computed remaining time from the
  // scheduled block's end), kick the timer immediately. The user
  // doesn't need to press Begin — the schedule already said this
  // is when to work, the app just clocks it.
  const autoStartMinutes = focus?.auto_start_minutes ?? null;
  const autoCompleteAtEnd = focus?.auto_complete_at_end ?? false;
  const focusEffortMinutes = focus?.effort_minutes ?? null;
  const focusKey = focus?.id ?? null;
  useEffect(() => {
    if (!autoStartMinutes || autoStartMinutes <= 0) return;
    if (timerState !== "idle" || secondsLeft > 0) return;
    const remainingSeconds = Math.max(1, Math.round(autoStartMinutes * 60));
    // Full block duration anchors the progress bar — `effort_minutes`
    // matches the scheduled block's length. If for any reason that
    // isn't on the focus, fall back to the remaining itself so the
    // bar is at least sane (starts at 0, fills as time runs out).
    const fullBlockSeconds = Math.max(
      remainingSeconds,
      Math.round((focusEffortMinutes ?? 0) * 60) || remainingSeconds,
    );
    startTimerWithRemaining(remainingSeconds, fullBlockSeconds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, autoStartMinutes]);

  // Active-block auto-complete: when the timer hits zero AND the
  // focus came from a scheduled block (not a user-initiated Begin),
  // commit the completion automatically. The user sees the same
  // celebration overlay as a manual Done — no extra click needed.
  useEffect(() => {
    if (!autoCompleteAtEnd) return;
    if (timerState !== "done") return;
    if (!focus?.id) return;
    if (celebrationTimeoutRef.current) return;
    setTimerState("celebrating");
    celebrationTimeoutRef.current = setTimeout(() => {
      celebrationTimeoutRef.current = null;
      const id = focus.id;
      if (!id) return;
      void (async () => {
        setCompleting(true);
        try {
          await onComplete?.(id);
        } finally {
          setCompleting(false);
        }
      })();
    }, CELEBRATION_HOLD_MS);
  }, [autoCompleteAtEnd, focus?.id, onComplete, timerState]);

  const pauseTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setTimerState("idle");
    // Keep secondsLeft as-is so resume starts from where we paused
  }, []);

  const resumeTimer = useCallback(() => {
    if (secondsLeft <= 0) return;
    setTimerState("running");
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;
          setTimerState("done");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [secondsLeft]);

  const progress = totalSeconds.current > 0
    ? Math.max(0, 1 - secondsLeft / totalSeconds.current)
    : 0;

  // Fires the celebration overlay then commits onComplete after the hold.
  // Used by both "Already finished" and the "Mark done" state of the
  // primary button. Declared BEFORE the empty-state early return so the
  // hook order stays unconditional.
  const triggerCelebration = useCallback(() => {
    if (!focus?.id) return;
    if (timerState === "celebrating") return;
    pauseTimer();
    setTimerState("celebrating");
    celebrationTimeoutRef.current = setTimeout(() => {
      celebrationTimeoutRef.current = null;
      void (async () => {
        if (!focus.id) return;
        setCompleting(true);
        try {
          await onComplete?.(focus.id);
        } finally {
          setCompleting(false);
        }
      })();
    }, CELEBRATION_HOLD_MS);
  }, [focus?.id, onComplete, pauseTimer, timerState]);

  if (!focus) {
    return (
      <section
        className="rounded-[28px] border border-border bg-card px-5 py-5 shadow-sm"
        data-testid="restart-block"
      >
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
          Start here
        </p>
        <h2 className="mt-2 text-xl font-semibold text-text">Nothing is clearly up next</h2>
        <p className="mt-2 text-sm leading-relaxed text-text-light">
          Today is waiting for one believable move from your schedule or open work before it tells you to push.
        </p>
      </section>
    );
  }

  // The goal-row time range is rendered ONLY when the focus carries a
  // real `timing_label` parsed out of the schedule layer. Without this
  // guard the previous fallback ("next 5-minute slot from now")
  // fabricated a time that referred to nothing in the database — a
  // restart card scheduled for tomorrow morning would silently render
  // "7:40 — 8:10" tonight just because the user opened the app at
  // 7:35 PM. The eyebrow row above already conditionally shows
  // `timing_label`, so omitting the right-hand range here when no
  // real timing exists keeps the surface honest.
  const durationMin = focus.effort_minutes ?? 30;
  const goalRowTimeRange = (() => {
    if (!focus.timing_label) return null;
    const match = focus.timing_label.match(/(\d{1,2}):(\d{2})\s*(am|pm)?/i);
    if (!match) return null;
    let hours = Number(match[1]);
    const mins = Number(match[2]);
    const meridiem = (match[3] ?? "").toLowerCase();
    if (meridiem === "pm" && hours < 12) hours += 12;
    if (meridiem === "am" && hours === 12) hours = 0;
    return formatTimeRange(hours * 60 + mins, durationMin);
  })();

  // Goals often already have "goal" in the title (e.g. "Worth parking
  // article goal"). Strip a trailing "goal" before we append our own
  // " goal" suffix, otherwise the label reads "FOR YOUR X GOAL GOAL".
  const goalLabel = focus.goal
    ? sanitizeSurfaceTitle(focus.goal.title)
        .replace(/\s+goal\s*$/i, "")
        .trim() || "this"
    : null;

  const headerStatus =
    timerState === "running"
      ? "Focusing"
      : timerState === "done"
        ? "Time\u2019s up"
        : secondsLeft > 0
          ? "Paused"
          : "Start here";

  const beginButtonLabel = (() => {
    if (completing) return "Completing\u2026";
    if (timerState === "done") return "Mark done";
    if (timerState === "running") return `Pause \u00b7 ${formatCountdown(secondsLeft)}`;
    if (timerState === "idle" && secondsLeft > 0)
      return `Resume \u00b7 ${formatCountdown(secondsLeft)}`;
    if (effectiveMinutes) return `Begin \u00b7 ${effectiveMinutes} minutes`;
    return "Begin";
  })();


  const handleBeginClick = () => {
    if (completing || !focus.id || timerState === "celebrating") return;
    if (timerState === "done") {
      triggerCelebration();
      return;
    }
    if (timerState === "running") {
      pauseTimer();
      return;
    }
    if (timerState === "idle" && secondsLeft > 0) {
      resumeTimer();
      return;
    }
    if (effectiveMinutes) {
      startTimer(effectiveMinutes);
      return;
    }
    // No duration on the task and the user hasn't picked one yet —
    // open the preset picker so they commit to a real session length
    // instead of starting a silent fake-30-min timer.
    setShowDurationPicker((current) => !current);
  };

  const handlePickDuration = (minutes: number) => {
    setChosenMinutes(minutes);
    setShowDurationPicker(false);
    startTimer(minutes);
  };

  const handleAlreadyFinished = async () => {
    if (!focus.id || completing || timerState === "celebrating") return;
    triggerCelebration();
  };

  const handleSkipToday = async () => {
    if (!focus.id || skipping || !onSkip) return;
    setSkipping(true);
    pauseTimer();
    try {
      await onSkip(focus.id);
    } finally {
      setSkipping(false);
    }
  };

  return (
    <section
      className="relative rounded-[28px] border border-border bg-card px-5 py-5 shadow-sm overflow-hidden"
      data-testid="restart-block"
      data-reveal-id={focus.id ?? undefined}
    >
      {/* Done celebration overlay — rings + checkmark + "One done." */}
      <AnimatePresence>
        {timerState === "celebrating" && (
          <DoneCelebration completedToday={completedToday} />
        )}
      </AnimatePresence>

      {/* Card body wrapper — fades + scales down while celebrating so the
          overlay reads as the focal moment */}
      <motion.div
        animate={{
          opacity: timerState === "celebrating" ? 0 : 1,
          scale: timerState === "celebrating" ? 0.98 : 1,
        }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        style={{ pointerEvents: timerState === "celebrating" ? "none" : "auto" }}
      >
      {/* Top metadata row — START HERE on left, duration · confidence on right */}
      <div className="flex items-start justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
          {headerStatus}
          {focus.timing_label ? ` \u00b7 ${focus.timing_label}` : ""}
        </p>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
          {focus.effort_minutes ? `${focus.effort_minutes} min` : null}
          {focus.effort_minutes && focus.confidence ? " \u00b7 " : null}
          {focus.confidence}
        </p>
      </div>

      {/* Big title — Fraunces editorial serif, slight negative tracking */}
      <h2 className="mt-3 font-serif text-2xl font-medium leading-tight tracking-tight text-text">
        {sanitizeSurfaceTitle(focus.title)}
      </h2>

      {/* Body line — stay with it / nice work / paused / why now */}
      <p className="mt-2 text-sm leading-relaxed text-text-light">
        {timerState === "running"
          ? "Stay with it. You\u2019re in the zone."
          : timerState === "done"
            ? "Nice work! Mark it done or keep going."
            : secondsLeft > 0
              ? `${formatCountdown(secondsLeft)} remaining. Resume when ready.`
              : cleanWhyNow(sanitizeSurfaceTitle(focus.why_now))}
      </p>

      {/* Progress bar while running / paused */}
      {(timerState === "running" || (timerState === "idle" && secondsLeft > 0)) && (
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-border">
          <div
            className="h-full rounded-full bg-forest-med transition-[width] duration-1000 ease-linear"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      )}

      {/* Goal section break — "FOR YOUR X GOAL" left, time range right.
          Right-hand range is omitted when the focus has no real timing
          so we never invent a clock value that refers to nothing. */}
      {focus.goal && goalLabel && (
        <div className="mt-4 flex items-center justify-between gap-2 border-t border-border/60 pt-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted truncate">
            For your {goalLabel} goal
          </p>
          {goalRowTimeRange && (
            <p className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
              {goalRowTimeRange}
            </p>
          )}
        </div>
      )}

      {/* Big primary action */}
      {focus.id && (
        <div className="mt-4">
          <button
            type="button"
            disabled={completing || skipping}
            onClick={handleBeginClick}
            className={`w-full rounded-2xl py-3 text-sm font-semibold text-white transition-colors disabled:opacity-60 ${
              timerState === "done" ? "bg-accent hover:bg-accent/90" : "bg-forest hover:bg-forest-med"
            }`}
            data-testid="restart-begin-button"
            aria-expanded={showDurationPicker || undefined}
          >
            {beginButtonLabel}
          </button>
          {/* Duration preset picker — only when the task has no
              effort_minutes AND the user hasn't picked one yet AND
              the timer hasn't started. The user must commit to a
              real session length before we run the clock. */}
          {showDurationPicker && timerState === "idle" && secondsLeft === 0 && (
            <div
              className="mt-3 rounded-2xl border border-border bg-bg/60 px-3 py-3"
              data-testid="restart-duration-picker"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
                How long?
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {[15, 25, 30, 45, 60].map((mins) => (
                  <button
                    key={mins}
                    type="button"
                    onClick={() => handlePickDuration(mins)}
                    className="rounded-full border border-border bg-white px-3 py-1.5 text-[12px] font-semibold text-text-light transition-colors hover:border-forest/40 hover:bg-sage-pale/60 hover:text-forest"
                  >
                    {mins} min
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer text links — padded to a 44px minimum touch target.
          The audit measured these at ~16px tall (text-only), which
          fails Apple HIG and WCAG 2.5.5. Negative horizontal margins
          on the row keep visual padding while growing the actual
          hit area to the full row edges. */}
      {focus.id && (
        <div className="mt-1 -mx-2 flex items-center justify-between text-xs">
          {onSkip ? (
            <button
              type="button"
              onClick={() => void handleSkipToday()}
              disabled={skipping || completing}
              className="min-h-[44px] px-3 py-3 text-muted transition-colors hover:text-text disabled:opacity-50"
              data-testid="restart-skip-today"
            >
              {skipping ? "Skipping\u2026" : "Skip today"}
            </button>
          ) : (
            <span />
          )}
          {onComplete && (
            <button
              type="button"
              onClick={() => void handleAlreadyFinished()}
              disabled={completing || skipping}
              className="min-h-[44px] px-3 py-3 text-muted transition-colors hover:text-text disabled:opacity-50"
              data-testid="restart-already-finished"
            >
              {completing ? "Saving\u2026" : "Already finished"}
            </button>
          )}
        </div>
      )}
      </motion.div>
    </section>
  );
}

interface DoneCelebrationProps {
  completedToday?: number;
}

// Editorial done celebration — quiet typography only, no rings or check.
// Small downward chevron above an editorial Fraunces line ("One done.")
// and a small-caps eyebrow ("MOMENTUM'S YOURS"). Held for ~1.5s before
// the parent removes this card and the next focus slides in.
function DoneCelebration({ completedToday: _ }: DoneCelebrationProps) {
  return (
    <motion.div
      className="absolute inset-0 z-10 flex flex-col items-center justify-center px-5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      data-testid="restart-done-celebration"
    >
      {/* Small downward chevron — decorative editorial mark */}
      <motion.svg
        width="14"
        height="8"
        viewBox="0 0 14 8"
        fill="none"
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
      >
        <path
          d="M1 1 L7 7 L13 1"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-forest/70"
        />
      </motion.svg>

      {/* "One done." — Fraunces editorial */}
      <motion.p
        className="mt-3 font-serif text-3xl font-medium tracking-tight text-forest"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        One done.
      </motion.p>

      {/* Small-caps eyebrow */}
      <motion.p
        className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-forest/60"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.45 }}
      >
        Momentum&apos;s yours
      </motion.p>
    </motion.div>
  );
}
